import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    makeStyles,
    Box,
    Theme,
    Typography,
    Button,
    FormHelperText,
} from '@material-ui/core';
import { SectionList } from './SectionList';
import { Comments } from './Comments';
import { PurchaseReasons } from './PurchaseReasons';
import { Signatures } from './Signatures';
import { IEvaluationForm, IForm } from './types';
import { Capitalize } from 'src/utils/capitalize';
import { Formik, FormikHelpers } from 'formik';
import useLead from 'src/hooks/useLead';
import { TSection, TTradeInVehicle } from 'src/interfaces/evaluation';
import { Spinner } from 'src/components/Spinners/followTheLead/spinner';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
    },
    leadInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    submitButton: {
        height: 60,
    },
    spinner: {
        paddingRight: 10,
    },
    spinnerContainer: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: theme.spacing(2),
    },
}));

export const EvaluationForm = ({
    lead,
    setOpen,
    isComplete,
    ...props
}: IEvaluationForm) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { updateLead, loading } = useLead();
    const { name, email, phone, vehicle } = lead;
    const { enqueueSnackbar } = useSnackbar();
    const isEditable = lead?.evaluation?.reviewDate
        ? moment().isSame(moment(lead?.evaluation?.reviewDate), 'day')
        : true;

    const handleSubmit = async (
        values: IForm,
        { setErrors, setStatus, setSubmitting }: FormikHelpers<IForm>
    ) => {
        try {
            const { agent } = lead;

            const currentAgent = {
                id: agent?._id,
                name: agent?.name,
                email: agent?.email,
                image: agent?.image,
            };

            await updateLead(
                {
                    evaluation: {
                        ...values,
                        user: lead.evaluation?.user || currentAgent,
                    },
                },
                lead?._id
            );

            setStatus({ success: true });
            setSubmitting(false);
            enqueueSnackbar(t('SnackBar.EvaluationUpdated'), {
                variant: 'success',
            });
            setOpen(false);
        } catch (error) {
            setStatus({ success: false });
            setSubmitting(false);
            if (error instanceof Error) setErrors({ submit: error.message });
        }
    };

    return (
        <Box className={classes.root} {...props}>
            <Box className={classes.leadInfo}>
                <Typography variant="body1" color="textPrimary">
                    <b>{t('evaluation.name')}:</b> {Capitalize(name)}
                </Typography>

                <Typography variant="body1" color="textPrimary">
                    <b>{t('evaluation.mail')}:</b> {email}
                </Typography>

                <Typography variant="body1" color="textPrimary">
                    <b>{t('evaluation.phone')}:</b> {phone}
                </Typography>

                <Typography variant="body1" color="textPrimary">
                    <b>{t('evaluation.vehicle')}:</b>{' '}
                    {`${Capitalize(vehicle?.make?.name)} ${Capitalize(
                        vehicle?.model
                    )} ${lead?.year}`}
                </Typography>
            </Box>

            <Formik
                enableReinitialize
                initialValues={{
                    sections: lead?.evaluation?.sections || [],
                    tradeInVehicle: lead?.evaluation?.tradeInVehicle || null,
                    comments: lead?.evaluation?.comments || '',
                    purchaseReasons: lead?.evaluation?.purchaseReasons || [],
                    signs: lead?.evaluation?.signs || [],
                    errors: '',
                    submit: '',
                }}
                onSubmit={handleSubmit}
            >
                {({
                    errors,
                    handleChange,
                    handleSubmit,
                    values,
                    setFieldValue,
                    isSubmitting,
                }) => {
                    const handleQuestionChange = (
                        questionId: string,
                        answer: boolean,
                        reason: string
                    ) => {
                        const updatedSections = values.sections.map(
                            (section: TSection) => ({
                                ...section,
                                questions: section.questions.map((question) =>
                                    question._id === questionId
                                        ? { ...question, answer, reason }
                                        : question
                                ),
                            })
                        );
                        setFieldValue('sections', updatedSections);
                    };

                    const handleChangeVehicle = (
                        name: string,
                        value: TTradeInVehicle
                    ) => {
                        setFieldValue(`tradeInVehicle.${name}`, value);
                    };

                    return (
                        <form onSubmit={handleSubmit}>
                            <Box className={classes.root}>
                                <SectionList
                                    sections={values.sections}
                                    vehicle={values.tradeInVehicle}
                                    handleChange={handleQuestionChange}
                                    handleChangeVehicle={handleChangeVehicle}
                                    isEditable={isEditable}
                                />

                                <Comments
                                    comment={values.comments}
                                    handleChange={handleChange}
                                    isEditable={isEditable}
                                />

                                <PurchaseReasons
                                    purchaseReasons={values.purchaseReasons}
                                    setFieldValue={setFieldValue}
                                    isEditable={isEditable}
                                />

                                <Signatures
                                    signs={values.signs}
                                    setFieldValue={setFieldValue}
                                    isEditable={isEditable}
                                />

                                {errors.submit && (
                                    <Box mt={3}>
                                        <FormHelperText error>
                                            {errors.submit}
                                        </FormHelperText>
                                    </Box>
                                )}

                                {loading ? (
                                    <Box className={classes.spinnerContainer}>
                                        <Spinner size={12} />
                                    </Box>
                                ) : (
                                    <>
                                        {!isEditable && (
                                            <Alert severity="error">
                                                {t(
                                                    'evaluation.evaluationNotEditable'
                                                )}
                                            </Alert>
                                        )}
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            type="submit"
                                            disabled={
                                                isSubmitting || !isEditable
                                            }
                                            className={classes.submitButton}
                                            fullWidth
                                        >
                                            {t('evaluation.submit')}
                                        </Button>
                                    </>
                                )}
                            </Box>
                        </form>
                    );
                }}
            </Formik>
        </Box>
    );
};
